import { useMemo, useState } from "react"
import { observer } from "mobx-react"
import {
  Col,
  Flex,
  Form,
  FormInstance,
  Row,
  Select,
  Switch,
  Typography,
} from "antd"

import { authStore, Merchant } from "@src/app/authStore"
import { DeleteConfirm } from "@src/app/components/DeleteConfirm"
import { notify } from "@src/app/components/notify"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import {
  conversionFormOptions,
  conversionFormOptionsIcons,
} from "@src/pages/settingsAutoConversion/constants"
import { SubmitButton } from "@src/shared/componemts/SubmitButton"

type Props = {
  form: FormInstance
  isLoading: boolean
  merch: Merchant
  submit: (req: unknown) => void
}
export const ConversionForm = observer(
  ({ form, isLoading, merch, submit }: Props) => {
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
    const symbol = Form.useWatch("symbol", form)
    const submitIsDisabled = useMemo(() => {
      return symbol === merch.exchange_to?.symbol
    }, [symbol])

    const currencies = GlobalCurrenciesCollection()
    const options = conversionFormOptions.map((i) => {
      const c = currencies.getBySymbolNetwork({
        network: i.network ?? "",
        symbol: i.value,
      })

      return {
        ...c,
        ...i,
      }
    })

    const showDeleteConfirm = () => {
      setOpenDeleteConfirm(true)
    }

    const onDeleteConfirm = () => {
      submit(form.getFieldsValue())
      setOpenDeleteConfirm(false)
    }

    const onSwitch = async (value: boolean) => {
      notify.error("You are not allowed to choose disable auto conversion")
    }

    return (
      <Form
        disabled={isLoading}
        form={form}
        id="GENERATED_ID_CONVERSIONFORM_FORM_VOID_1"
        onFinish={showDeleteConfirm}
        style={{ borderBottom: "1px solid #E9F1EA", paddingBottom: 16 }}
      >
        <Typography.Title level={4} style={{ marginBottom: 16, marginTop: 0 }}>
          Conversion
        </Typography.Title>

        <Row gutter={16}>
          <Col span={16}>
            <Flex
              align="center"
              gap={16}
              id="GENERATED_ID_CONVERSIONFORM_BLOCK"
            >
              <Form.Item id="GENERATED_ID_CONVERSIONFORM_TSX_FORM.ITEM_VOID_1">
                <Switch checked={true} onChange={onSwitch} />
              </Form.Item>

              <Typography.Text style={{ fontSize: 16 }}>
                Auto conversion of incoming crypto currencies
              </Typography.Text>
            </Flex>
          </Col>

          <Col span={8}>
            <Flex gap={16}>
              <Form.Item
                id="GENERATED_ID_CONVERSIONFORM_TSX_FORM.ITEM_VOID_2"
                initialValue={merch.exchange_to?.symbol}
                name="symbol"
                style={{ flex: 1 }}
              >
                <Select
                  disabled={isLoading}
                  id="GENERATED_ID_CONVERSIONFORM_SELECT_VOID_1"
                  placeholder="Currency"
                  size="large"
                >
                  {options.map(({ label, network, symbol_alias, value }) => (
                    <Select.Option key={value} value={value}>
                      <Flex align="center" gap={8}>
                        <img
                          alt={value}
                          src={conversionFormOptionsIcons[value]}
                          style={isLoading ? { filter: "grayscale(100%)" } : {}}
                        />
                        <Typography.Text disabled={isLoading}>
                          {symbol_alias
                            ? `${symbol_alias} (${network})`
                            : label}
                        </Typography.Text>
                      </Flex>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <SubmitButton
                buttonProps={{
                  disabled: submitIsDisabled,
                  loading: isLoading,
                  size: "large",
                  type: "primary",
                }}
                form={form}
              >
                Save
              </SubmitButton>
            </Flex>
          </Col>
        </Row>

        <DeleteConfirm
          cancelText="Cancel"
          isLoading={isLoading}
          okText="Confirm"
          onCancel={() => setOpenDeleteConfirm(false)}
          onOk={onDeleteConfirm}
          open={openDeleteConfirm}
          text={
            <Typography.Text style={{ fontSize: 16, textAlign: "center" }}>
              All changes will take effect within 24 hours.
              <br />
              Are you sure you want to proceed?
            </Typography.Text>
          }
          title="Notice"
        />
      </Form>
    )
  },
)
